import { useEffect, useRef, useState, type CSSProperties } from 'react'
import type { Card, Suit } from './game'
import { pipRowsFor } from './cardLayout'
import { BLOKKER, borderColorForTurns } from './blokker'

interface Props {
  card: Card
  className?: string
  style?: CSSProperties
}

function useAnimatedNumber(target: number, duration: number = 600): number {
  const [displayed, setDisplayed] = useState(target)
  const fromRef = useRef(target)

  useEffect(() => {
    if (target === fromRef.current) return
    const from = fromRef.current
    const start = performance.now()
    let frameId = 0
    const animate = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const value = progress < 1 ? Math.round(from + (target - from) * eased) : target
      fromRef.current = value
      setDisplayed(value)
      if (progress < 1) frameId = requestAnimationFrame(animate)
    }
    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [target, duration])

  return displayed
}

function counterColor(remaining: number): string {
  const t = Math.max(0, Math.min(1, remaining / BLOKKER.STAGE_3_DEMAND))
  const hue = (1 - t) * 130
  return `hsl(${hue}, 80%, 62%)`
}

function SuitGlyph({ suit }: { suit: Suit }) {
  switch (suit) {
    case 'spades':
      return (
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <path d="M50 8 C 14 44 10 68 28 78 C 40 84 50 76 50 68 C 50 76 60 84 72 78 C 90 68 86 44 50 8 Z M 40 82 L 60 82 L 56 94 L 44 94 Z" fill="currentColor"/>
        </svg>
      )
    case 'hearts':
      return (
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <path d="M50 92 C 8 62 8 28 30 24 C 42 22 48 30 50 38 C 52 30 58 22 70 24 C 92 28 92 62 50 92 Z" fill="currentColor"/>
        </svg>
      )
    case 'diamonds':
      return (
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <path d="M50 8 L 86 50 L 50 92 L 14 50 Z" fill="currentColor"/>
        </svg>
      )
    case 'clubs':
      return (
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="30" r="18" fill="currentColor"/>
          <circle cx="30" cy="58" r="18" fill="currentColor"/>
          <circle cx="70" cy="58" r="18" fill="currentColor"/>
          <path d="M 40 80 L 60 80 L 56 94 L 44 94 Z" fill="currentColor"/>
        </svg>
      )
  }
}

function rankLabel(rank: Card['rank']): string {
  switch (rank) {
    case 'jack':  return 'J'
    case 'queen': return 'Q'
    case 'king':  return 'K'
    case 'ace':   return 'A'
    default:      return rank
  }
}

function PipArt({ suit, rank }: { suit: Suit; rank: Card['rank'] }) {
  const rows = pipRowsFor(rank)
  if (!rows) return null
  return (
    <div className="card-pips">
      {rows.map((count, rowIdx) => (
        <div key={rowIdx} className="card-pip-row">
          {Array.from({ length: count }, (_, pipIdx) => (
            <div key={pipIdx} className="card-pip">
              <SuitGlyph suit={suit}/>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

function JackHelm() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <path d="M50 20 Q46 10 42 2 Q48 4 50 14 Q52 4 58 2 Q54 10 50 20" fill="currentColor"/>
      <path d="M20 50 Q20 24 50 22 Q80 24 80 50 L80 66 L20 66 Z" fill="currentColor"/>
      <rect x="26" y="42" width="48" height="4" fill="#fdfdfb"/>
      <path d="M26 66 Q26 78 50 80 Q74 78 74 66 Z" fill="currentColor"/>
    </svg>
  )
}

function QueenTiara() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <path d="M14 60 L22 26 L34 48 L50 14 L66 48 L78 26 L86 60 Z" fill="currentColor"/>
      <circle cx="24" cy="24" r="3.5" fill="currentColor"/>
      <circle cx="50" cy="12" r="4" fill="currentColor"/>
      <circle cx="76" cy="24" r="3.5" fill="currentColor"/>
      <rect x="14" y="64" width="72" height="6" rx="2" fill="currentColor"/>
    </svg>
  )
}

function KingCrown() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <path d="M6 58 L10 14 L26 42 L42 6 L50 18 L58 6 L74 42 L90 14 L94 58 Z" fill="currentColor"/>
      <rect x="4" y="58" width="92" height="18" rx="3" fill="currentColor"/>
      <circle cx="24" cy="67" r="3.5" fill="#fdfdfb"/>
      <circle cx="50" cy="67" r="4.5" fill="#fdfdfb"/>
      <circle cx="76" cy="67" r="3.5" fill="#fdfdfb"/>
    </svg>
  )
}

function FaceEmblem({ rank }: { rank: Card['rank'] }) {
  switch (rank) {
    case 'jack':  return <JackHelm/>
    case 'queen': return <QueenTiara/>
    case 'king':  return <KingCrown/>
    default:      return null
  }
}

function Stage3Card({ remaining, className, style }: { remaining: number; className?: string; style?: CSSProperties }) {
  const displayed = useAnimatedNumber(remaining, 600)
  return (
    <div
      className={className}
      style={style}
      role="img"
      aria-label={`blokker, ${displayed} points to clear`}
    >
      <div className="card-blokker-counter" style={{ color: counterColor(displayed) }}>{displayed}</div>
    </div>
  )
}

export function PlayingCard({ card, className, style }: Props) {
  const blokker = card.blokker

  if (blokker?.stage === 3) {
    const classes = ['card-face', 'card-blokker-3', className].filter(Boolean).join(' ')
    return <Stage3Card remaining={blokker.remaining} className={classes} style={style} />
  }

  const warnClass = blokker
    ? `card-blokker-warn-${borderColorForTurns(blokker.turns)}`
    : ''
  const classes = ['card-face', warnClass, className].filter(Boolean).join(' ')

  return (
    <div
      className={classes}
      style={style}
      data-suit={card.suit}
      role="img"
      aria-label={`${card.rank} of ${card.suit}`}
    >
      <div className="card-rank">{rankLabel(card.rank)}</div>
      <div className="card-art">
        {pipRowsFor(card.rank)
          ? <PipArt suit={card.suit} rank={card.rank}/>
          : card.rank === 'ace'
            ? <div className="card-ace"><SuitGlyph suit={card.suit}/></div>
            : (card.rank === 'jack' || card.rank === 'queen' || card.rank === 'king')
              ? <FaceEmblem rank={card.rank}/>
              : null}
      </div>
      <div className="card-suit"><SuitGlyph suit={card.suit}/></div>
      {blokker && (
        <div className="card-blokker-turns">{blokker.turns}</div>
      )}
    </div>
  )
}
